import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock, Send, ArrowUpRight, Star } from 'lucide-react';
import SectionEyebrow from '../components/SectionEyebrow';
import { usePage, img } from '../lib/content';
import SmartMedia from '../components/SmartMedia';
import Seo from '../components/Seo';
import { api } from '../lib/api';
import { toast } from 'sonner';

const Contact = () => {
  const c = usePage('contact');
  const hours = Array.isArray(c.hours) ? c.hours : [];
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.message) {
      toast.error('Please fill all required fields.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/api/leads', { type: 'contact', ...form });
      toast.success("Message sent! We'll reply within one business day.");
      setForm({ firstName: '', lastName: '', email: '', phone: '', message: '' });
    } catch {
      toast.error('Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#f7f1e3]">
      <Seo title="Contact & Directions · Millpond Gatherings, Northford CT" description="Visit, call, or message Millpond Gatherings at 1565 Middletown Ave, Northford, CT. Hours, directions, and reservations." path="/contact" breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }]} />
      {/* HERO */}
      <section className="relative h-[58vh] min-h-[420px] w-full overflow-hidden">
        <SmartMedia src={c.heroImage} alt="Millpond Gatherings — visit us in Northford, CT" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#f7f1e3] via-transparent to-transparent" style={{ backgroundSize: '100% 30%', backgroundRepeat: 'no-repeat', backgroundPosition: 'bottom' }} />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-28">
          <SectionEyebrow light>{c.eyebrow}</SectionEyebrow>
          <h1 className="font-serif text-[#f7f1e3] text-[48px] md:text-[72px] lg:text-[92px] leading-[1.02] tracking-[-0.02em] mt-4 max-w-4xl">
            {c.title}
          </h1>
          {c.intro && (
            <p className="mt-6 text-[#f1ead9]/90 max-w-2xl text-base md:text-lg leading-relaxed">
              {c.intro}
            </p>
          )}
        </div>
      </section>

      {/* INFO CARDS */}
      <section className="pb-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          <InfoCard icon={MapPin} label="Visit" testid="contact-card-address">
            <p className="font-serif text-[#942222] text-xl lg:text-2xl leading-snug">{c.address}</p>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.address || '')}`}
              target="_blank" rel="noreferrer"
              className="mt-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#b8923e] hover:gap-2.5 transition-all"
            >
              Get Directions <ArrowUpRight size={14} />
            </a>
            {c.googleReviewUrl && (
              <a
                href={c.googleReviewUrl}
                target="_blank" rel="noreferrer"
                data-testid="contact-google-review"
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#b8923e] hover:gap-2.5 transition-all"
              >
                <Star size={14} /> Review us on Google
              </a>
            )}
          </InfoCard>

          <InfoCard icon={Phone} label="Call" testid="contact-card-phone">
            <a href={`tel:${c.phoneRaw}`} className="font-serif text-[#942222] text-xl lg:text-2xl block hover:opacity-70 transition">{c.phone}</a>
            {c.email && (
              <a href={`mailto:${c.email}`} className="mt-4 inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[#b8923e] hover:gap-2.5 transition-all">
                <Mail size={14} /> {c.email}
              </a>
            )}
          </InfoCard>

          <InfoCard icon={Clock} label="Hours" testid="contact-card-hours">
            <ul className="space-y-2">
              {hours.map((h, i) => (
                <li key={i} className="flex items-baseline justify-between gap-3 text-[14px]">
                  <span className="text-[#942222]/70">{h.label}</span>
                  <span className="font-serif text-[#942222] text-[15px] whitespace-nowrap">{h.value}</span>
                </li>
              ))}
            </ul>
          </InfoCard>
        </div>
      </section>

      {/* MAP + FORM */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          {/* MAP */}
          <div className="lg:col-span-5 min-h-[360px] lg:min-h-0">
            <div className="h-full w-full overflow-hidden border border-[#942222]/15 bg-[#efe6d0]">
              {c.mapUrl ? (
                <iframe
                  title="Millpond Gatherings location"
                  src={img(c.mapUrl)}
                  data-testid="contact-map"
                  className="w-full h-full min-h-[360px]"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              ) : (
                <div className="h-full flex items-center justify-center text-[#942222]/50 text-sm">Map unavailable</div>
              )}
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="bg-[#942222] text-[#f1ead9] p-8 lg:p-12">
              <h2 className="font-serif text-3xl lg:text-4xl tracking-tight">{c.formTitle}</h2>
              {c.formNote && <p className="mt-2 text-[#f1ead9]/70 text-sm">{c.formNote}</p>}
              <form onSubmit={onSubmit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-7">
                <Field label="First Name *" name="firstName" value={form.firstName} onChange={onChange} testid="contact-first-name" />
                <Field label="Last Name *" name="lastName" value={form.lastName} onChange={onChange} testid="contact-last-name" />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} testid="contact-email" />
                <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} testid="contact-phone" />
                <div className="md:col-span-2">
                  <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">Message *</label>
                  <textarea name="message" rows={5} value={form.message} onChange={onChange} data-testid="contact-message" className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors resize-none" />
                </div>
                <div className="md:col-span-2">
                  <button type="submit" disabled={submitting} data-testid="contact-submit" className="inline-flex items-center gap-2 px-9 py-4 bg-[#e2b95a] hover:bg-[#cea444] text-[#942222] text-[11px] tracking-[0.28em] uppercase font-semibold transition-colors disabled:opacity-60">
                    <Send size={14} /> Send Message
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const InfoCard = ({ icon: Icon, label, children, testid }) => (
  <div data-testid={testid} className="bg-white/60 border border-[#942222]/10 p-8 lg:p-9 flex flex-col">
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 rounded-full bg-[#942222] flex items-center justify-center text-[#f1ead9] shrink-0">
        <Icon size={16} />
      </span>
      <p className="text-[11px] tracking-[0.28em] uppercase text-[#942222]/60">{label}</p>
    </div>
    <div className="mt-6">{children}</div>
  </div>
);

const Field = ({ label, name, type = 'text', value, onChange, testid }) => (
  <div>
    <label className="text-[11px] tracking-[0.22em] uppercase text-[#f1ead9]/70">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="mt-2 w-full bg-transparent border-b border-[#f1ead9]/40 py-3 text-[#f1ead9] outline-none focus:border-[#e2b95a] transition-colors"
    />
  </div>
);

export default Contact;
